import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import type { NoteDetail } from '@/api/types';
import {
  Button,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Modal,
  Notice,
  Page,
  PageHead,
  Tag,
  Table,
  TextArea,
} from '@/components/ui';
import { formatDateTime, formatRange, humanise } from '@/lib/format';

const EXCEPTION_STATUSES = new Set(['declined', 'not_applicable', 'unable']);

/**
 * ADM-03. Review a submitted note, and correct it without losing the original.
 *
 * Every correction preserves the original text, the editor, the timestamp, and
 * the reason — which is why the revision list is as prominent as the note.
 */
export function NoteDetailPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const { data, error, loading, reload } = useApi<NoteDetail>(`/admin/notes/${noteId}`);
  const [correcting, setCorrecting] = useState(false);

  if (loading) return <Loading />;
  if (error) return <Page><ErrorState message={error.message} onRetry={reload} /></Page>;
  if (!data) return null;

  const { note, visit, revisions } = data;

  return (
    <Page>
      <PageHead
        breadcrumb={
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/notes">Notes</Link>
            <span aria-hidden="true">/</span>
            <span>{visit?.recipient_name ?? 'Visit note'}</span>
          </nav>
        }
        title="Visit note"
        subtitle={
          visit
            ? `${visit.recipient_name} · ${visit.caregiver_name ?? 'Unassigned'} · ${formatRange(visit.scheduled_start, visit.scheduled_end)}`
            : undefined
        }
        actions={
          note.is_final ? (
            <Button variant="secondary" onClick={() => setCorrecting(true)}>
              Correct this note
            </Button>
          ) : undefined
        }
      />

      <div className="stack">
        <div className="row row--wrap">
          <Tag
            label={humanise(note.status)}
            tone={note.status === 'corrected' ? 'info' : note.is_final ? 'positive' : 'attention'}
          />
          <Tag label={`${humanise(note.input_method)} entry`} tone="neutral" />
          {note.input_method !== 'typed' ? (
            <Tag
              label={note.transcript_reviewed ? 'Transcript reviewed' : 'Not reviewed'}
              tone={note.transcript_reviewed ? 'positive' : 'urgent'}
            />
          ) : null}
          {note.has_concern_flag ? <Tag label="Concern flagged" tone="urgent" /> : null}
          {note.recorded_on_behalf ? <Tag label="Recorded on behalf" tone="info" /> : null}
          <span className="meta">Submitted {formatDateTime(note.submitted_at)}</span>
        </div>

        {note.recorded_on_behalf ? (
          <Notice tone="info" title="A coordinator wrote this note, not the caregiver">
            {visit?.caregiver_name ?? 'The caregiver'} delivered the care.{' '}
            {note.on_behalf_reason ? `Reason given: ${note.on_behalf_reason}` : null}
          </Notice>
        ) : null}

        {revisions.length > 0 ? (
          <Notice tone="info" title="This note has been corrected">
            The original text is preserved below, with who changed it and why.
          </Notice>
        ) : null}

        <Card variant="flush">
          <CardHead title="Planned tasks" />
          <Table columns={['Task', 'Status', 'Reason']}>
            {note.task_statuses.map((task) => (
              <tr key={task.task_id}>
                <td className="strong">{task.name || task.task_id}</td>
                <td>
                  <Tag
                    label={humanise(task.status)}
                    tone={
                      task.status === 'complete'
                        ? 'positive'
                        : EXCEPTION_STATUSES.has(task.status)
                          ? 'attention'
                          : 'neutral'
                    }
                  />
                </td>
                <td>{task.reason ?? <span className="meta">—</span>}</td>
              </tr>
            ))}
          </Table>
        </Card>

        <div className="grid-2">
          <Card>
            <h3>Visit summary</h3>
            <p className="meta" style={{ marginBottom: 'var(--space-2)' }}>
              Informs the family update.
            </p>
            <p>{note.summary ?? <span className="meta">Not recorded.</span>}</p>
          </Card>
          <Card>
            <h3>Observations</h3>
            <p className="meta" style={{ marginBottom: 'var(--space-2)' }}>
              Internal. Never shown to the family.
            </p>
            <p>{note.observations ?? <span className="meta">Not recorded.</span>}</p>
          </Card>
        </div>

        {revisions.length > 0 ? (
          <Card variant="flush">
            <CardHead title="Revision history" />
            <div className="card__body stack">
              {revisions.map((revision) => (
                <Card key={revision.revision_number}>
                  <div className="row row--between">
                    <span className="strong">Revision {revision.revision_number}</span>
                    <span className="meta">
                      {revision.editor} · {formatDateTime(revision.created_at)}
                    </span>
                  </div>
                  <p style={{ marginTop: 'var(--space-2)' }}>
                    <span className="kicker">Reason</span>
                    <br />
                    {revision.reason}
                  </p>
                  <div className="divider" />
                  <p className="kicker">Original text</p>
                  <p className="meta" style={{ whiteSpace: 'pre-line' }}>
                    {String(
                      (revision.previous_content as { summary?: string }).summary ?? '—',
                    )}
                  </p>
                </Card>
              ))}
            </div>
          </Card>
        ) : null}
      </div>

      {correcting ? (
        <CorrectionModal
          noteId={noteId!}
          currentSummary={note.summary ?? ''}
          currentObservations={note.observations ?? ''}
          onClose={() => setCorrecting(false)}
          onDone={() => {
            setCorrecting(false);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function CorrectionModal({
  noteId,
  currentSummary,
  currentObservations,
  onClose,
  onDone,
}: {
  noteId: string;
  currentSummary: string;
  currentObservations: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState(currentSummary);
  const [observations, setObservations] = useState(currentObservations);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setProblem(null);
    try {
      await api.post(`/admin/notes/${noteId}/correct`, {
        reason: reason.trim(),
        summary: summary.trim() || null,
        observations: observations.trim() || null,
      });
      onDone();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Correct this note" onClose={onClose} wide>
      <div className="stack">
        <Notice tone="info" title="The original is preserved">
          Your name, the time, and your reason are recorded alongside the original text. The
          family update is rebuilt and returns to the approval queue labelled as corrected.
        </Notice>

        {problem ? (
          <Notice tone="urgent" title="Could not save the correction">
            {problem}
          </Notice>
        ) : null}

        <Field label="Reason for the correction" error={reason.trim() ? null : 'A reason is required'}>
          {(props) => (
            <TextArea
              {...props}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Why is this being changed?"
              autoFocus
            />
          )}
        </Field>

        <Field label="Visit summary">
          {(props) => (
            <TextArea {...props} value={summary} onChange={(e) => setSummary(e.target.value)} />
          )}
        </Field>

        <Field label="Observations" hint="Internal. Not shown to the family.">
          {(props) => (
            <TextArea
              {...props}
              value={observations}
              onChange={(event) => setObservations(event.target.value)}
            />
          )}
        </Field>

        <div className="row row--between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} busy={busy} disabled={!reason.trim()}>
            Save correction
          </Button>
        </div>
      </div>
    </Modal>
  );
}
